import type { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { DataTableColumnHeader } from '@/components/data-table'
import { formatDateTime, formatLabel } from '@/lib/format'

export type AdminUserRow = {
  id: string
  email: string
  name: string
  username: string | null
  display_name: string | null
  role: 'user' | 'moderator' | 'admin' | 'superadmin'
  banned: boolean
  ban_reason: string | null
  ban_expires: number | null
  created_at: number
}

const ROLE_OPTIONS = [
  { label: 'User', value: 'user' },
  { label: 'Moderator', value: 'moderator' },
  { label: 'Admin', value: 'admin' },
  { label: 'Superadmin', value: 'superadmin' },
] as const

const STATUS_OPTIONS = [
  { label: 'Active', value: 'false' },
  { label: 'Banned', value: 'true' },
] as const

export const ADMIN_USER_SORT_IDS = [
  'email',
  'name',
  'role',
  'banned',
  'createdAt',
] as const

export const ADMIN_USER_FILTER_KEYS = ['role', 'banned', 'createdAt'] as const

export function createAdminUsersColumns(): ColumnDef<AdminUserRow, unknown>[] {
  return [
    {
      id: 'name',
      accessorKey: 'name',
      meta: { label: 'Name' },
      enableSorting: true,
      enableHiding: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Name" />
      ),
      cell: ({ row }) => {
        const u = row.original
        const label = u.display_name ?? u.name
        return (
          <div className="min-w-0 max-w-[14rem]">
            <div className="truncate text-sm font-medium">{label}</div>
            {u.username ? (
              <div className="truncate text-xs text-muted-foreground">
                @{u.username}
              </div>
            ) : null}
          </div>
        )
      },
    },
    {
      id: 'email',
      accessorKey: 'email',
      meta: { label: 'Email' },
      enableSorting: true,
      enableHiding: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Email" />
      ),
      cell: ({ row }) => (
        <span className="block max-w-[16rem] truncate text-sm">
          {row.original.email}
        </span>
      ),
    },
    {
      id: 'role',
      accessorKey: 'role',
      meta: {
        label: 'Role',
        variant: 'select',
        options: [...ROLE_OPTIONS],
      },
      enableSorting: true,
      enableHiding: true,
      enableColumnFilter: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Role" />
      ),
      cell: ({ row }) => (
        <Badge variant="secondary" className="font-normal">
          {formatLabel(row.original.role)}
        </Badge>
      ),
    },
    {
      id: 'banned',
      accessorKey: 'banned',
      meta: {
        label: 'Status',
        variant: 'select',
        options: [...STATUS_OPTIONS],
      },
      enableSorting: true,
      enableHiding: true,
      enableColumnFilter: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) =>
        row.original.banned ? (
          <Badge variant="destructive" className="font-normal">
            Banned
          </Badge>
        ) : (
          <Badge variant="outline" className="font-normal">
            Active
          </Badge>
        ),
    },
    {
      id: 'createdAt',
      accessorKey: 'created_at',
      meta: {
        label: 'Created',
        variant: 'dateRange',
      },
      enableSorting: true,
      enableHiding: true,
      enableColumnFilter: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Created" />
      ),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-sm text-muted-foreground">
          {formatDateTime(row.original.created_at)}
        </span>
      ),
    },
  ]
}
